import { Component, OnInit, OnDestroy } from '@angular/core';
import { firstValueFrom, Subscription } from 'rxjs';
import {
	EngineRubricSkill,
	EngineRubricSkillCreatePayload,
	EngineRubricSkillUpdatePayload,
	Rubric,
	RubricCreatePayload,
	RubricLevel,
	RubricLevelCreatePayload,
	RubricLevelUpdatePayload,
	RubricUpdatePayload,
} from '@mira-avant/mtlibs';
import { RubricService } from '../../../../services/rubric.service';
import { ProductService } from '../../../../services/product.service';
import { AngularModule } from '../../../../modules/angular.module';
import { ZorroModule } from '../../../../modules/zorro.module';
import { BreadcrumbsComponent } from '../../../ui/admin/breadcrumbs/breadcrumbs.component';
import { RubricFormComponent } from '../../../forms/standard/rubric-form/rubric-form.component';
import { RubricLevelFormComponent } from '../../../forms/standard/rubric-level-form/rubric-level-form.component';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { RubricsTableComponent } from '../../../ui/admin/rubrics-table/rubrics-table.component';
import { RubricLevelsTableComponent } from '../../../ui/admin/rubric-levels-table/rubric-levels-table.component';
import { EngineRubricSkillFormComponent } from '../../../forms/standard/engine-rubric-skill-form/engine-rubric-skill-form.component';
import { EngineRubricSkillTableComponent } from '../../../ui/admin/engine-rubric-skill-table/engine-rubric-skill-table.component';
import { EngineService } from '../../../../services/engine.service';

@Component({
	selector: 'app-rubrics',
	standalone: true,
	imports: [
		AngularModule,
		ZorroModule,
		NzCardModule,
		NzDividerModule,
		BreadcrumbsComponent,
		RubricFormComponent,
		RubricsTableComponent,
		RubricLevelFormComponent,
		RubricLevelsTableComponent,
		EngineRubricSkillFormComponent,
		EngineRubricSkillTableComponent,
	],
	templateUrl: './rubrics.component.html',
	styleUrl: './rubrics.component.scss',
})
export class RubricsComponent implements OnInit, OnDestroy {
	private subscriptions: Subscription = new Subscription();
	public showRubricForm: boolean = false;
	public showRubricLevelForm: boolean = false;
	public showEngineRubricSkillForm: boolean = false;

	constructor(
		public rubricService: RubricService,
		public productService: ProductService,
		public engineService: EngineService
	) {}

	ngOnInit(): void {
		this.subscriptions.add(
			this.rubricService.selectedRubric$.subscribe(rubric => {
				if (rubric) {
					this.showRubricForm = true;
				} else {
					this.showRubricForm = false;
				}
			})
		);
		this.subscriptions.add(
			this.rubricService.selectedRubricLevel$.subscribe(rubricLevel => {
				if (rubricLevel) {
					this.showRubricLevelForm = true;
				} else {
					this.showRubricLevelForm = false;
				}
			})
		);
		this.subscriptions.add(
			this.engineService.selectedEngineRubricSkill$.subscribe(prs => {
				if (prs) {
					this.showEngineRubricSkillForm = true;
				} else {
					this.showEngineRubricSkillForm = false;
				}
			})
		);
	}

	public toggleRubricForm(): void {
		this.showRubricForm = !this.showRubricForm;
	}

	public toggleRubricLevelForm(): void {
		this.showRubricLevelForm = !this.showRubricLevelForm;
	}

	public toggleEngineRubricSkillForm(): void {
		this.showEngineRubricSkillForm = !this.showEngineRubricSkillForm;
	}

	public async onRubricSubmit(payload: RubricCreatePayload | RubricUpdatePayload): Promise<void> {
		const selectedRubric: Rubric | undefined = await firstValueFrom(this.rubricService.selectedRubricBs$);
		if (selectedRubric && selectedRubric.code) {
			// Update existing rubric
			const updatePayload: RubricUpdatePayload = {
				code: payload.code || selectedRubric.code,
				label: payload.label || selectedRubric.label,
				display_order: payload.display_order || selectedRubric.display_order,
			};
			await this.rubricService.updateRubric$(selectedRubric.code, updatePayload);
		} else {
			if (
				!payload.code ||
				!payload.label ||
				payload.display_order === undefined ||
				payload.display_order === null
			) {
				throw new Error('Missing required fields for rubric creation');
			}
			const createPayload: RubricCreatePayload = {
				code: payload.code,
				label: payload.label,
				display_order: payload.display_order,
			};
			// Create new rubric
			await this.rubricService.createRubric$(createPayload);
		}
		this.showRubricForm = false;
		this.rubricService.selectRubric(undefined);
		this.rubricService.selectRubricLevel(undefined);
	}

	public async onRubricLevelSubmit(payload: RubricLevelCreatePayload | RubricLevelUpdatePayload): Promise<void> {
		const selectedRubricLevel: RubricLevel | undefined = await firstValueFrom(
			this.rubricService.selectedRubricLevelBs$
		);
		if (selectedRubricLevel && selectedRubricLevel.rubric_code) {
			// Update existing rubric level
			const updatePayload: RubricLevelUpdatePayload = {
				rubric_code: selectedRubricLevel.rubric_code,
				level: Number.isInteger(payload.level) ? payload.level : selectedRubricLevel.level,
				name: payload.name || selectedRubricLevel.name,
				short_description: payload.short_description || selectedRubricLevel.short_description,
				long_description: payload.long_description || selectedRubricLevel.long_description,
				prompt_description: payload.prompt_description || selectedRubricLevel.prompt_description,
				rubric_level_skills: payload.rubric_level_skills || undefined,
			};
			await this.rubricService.updateRubricLevel$(selectedRubricLevel.uuid, updatePayload);
		} else {
			if (
				!payload.rubric_code ||
				!Number.isInteger(payload.level) ||
				!payload.name ||
				!payload.short_description ||
				!payload.long_description ||
				!payload.prompt_description
			) {
				throw new Error('Missing required fields for rubric level creation');
			}
			const createPayload: RubricLevelCreatePayload = {
				rubric_code: payload.rubric_code,
				level: payload.level,
				name: payload.name,
				short_description: payload.short_description,
				long_description: payload.long_description,
				prompt_description: payload.prompt_description,
				rubric_level_skills: payload.rubric_level_skills || [],
			};
			// Create new rubric level
			await this.rubricService.createRubricLevel$(createPayload);
		}
		this.showRubricLevelForm = false;
		this.rubricService.selectRubricLevel(undefined);
	}

	public async onEngineRubricSkillSubmit(
		payload: EngineRubricSkillCreatePayload | EngineRubricSkillUpdatePayload
	): Promise<void> {
		const selectedPrs: EngineRubricSkill | undefined = await firstValueFrom(
			this.engineService.selectedEngineRubricSkillBs$
		);
		if (selectedPrs && selectedPrs.uuid) {
			// Update existing EngineRubricSkill
			const updatePayload: EngineRubricSkillUpdatePayload = {
				engine_name: payload.engine_name || selectedPrs.engine_name,
				rubric_code: payload.rubric_code || selectedPrs.rubric_code,
				skill_code: payload.skill_code || selectedPrs.skill_code,
				assessment: payload.assessment || selectedPrs.assessment,
			};
			await this.engineService.updateEngineRubricSkill$(selectedPrs.uuid, updatePayload);
		} else {
			if (!payload.engine_name || !payload.rubric_code || !payload.skill_code || !payload.assessment) {
				throw new Error('Missing required fields for EngineRubricSkill creation');
			}
			const createPayload: EngineRubricSkillCreatePayload = {
				engine_name: payload.engine_name,
				rubric_code: payload.rubric_code,
				skill_code: payload.skill_code,
				assessment: payload.assessment,
			};
			// Create new EngineRubricSkill
			await this.engineService.createEngineRubricSkill$(createPayload);
		}
		this.showEngineRubricSkillForm = false;
		this.engineService.selectEngineRubricSkill(undefined);
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}
}
